/*
 * File: ErrorComponent.jsx
 * Project: seesaw-marketplace-app
 *
 * Created by Brendan Michaelsen on February 19, 2022 at 12:17 AM
 * Copyright © 2022 Seesaw Technologies, LLC. All rights reserved.
 *
 * Last Modified: July 21, 2022 at 5:00 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React from 'react';
import PropTypes from 'prop-types';
import Lottie from 'react-lottie-player';
import { replaceColor } from 'lottie-colorify';

// Components
import { Meta } from '../Meta';
import { Typography } from '../Typography';
import { Button } from '../Button';

// Animation
import animationData from '../../assets/animations/bicycle.json';

// Styles
import * as S from './ErrorComponent.styles';


/**
 * Component
 */

export const ErrorComponent = ({
	locale, title, description, message, buttons, merchant
}) => (
	<>
		{/* Meta */}
		<Meta
			meta={{
				title: `Page Not Found | ${merchant.name}`,
				description: 'We searched high and low but couldn\'t find what you\'re looking for. Let\'s find a better place for you to go.',
				bodyClasses: 'hide-captcha',
				showSocial: false,
			}}
			locale={locale}
			merchant={merchant}
		/>

		{/* Component Content */}
		<S.Wrapper>
			<S.ContentContainer>
				<S.LottieLink identifier="error-center-lottie">
					<Lottie loop play animationData={replaceColor('#e8bb3b', merchant.colors.brandPrimaryBase, animationData)} style={{ height: 330 }} />
				</S.LottieLink>
				<S.MessageContainer>
					<Typography tag="h1" weight="semibold">
						{title || 'Whoops!'}
					</Typography>
					<S.DescriptionContainer>
						<Typography tag="h4" weight="light">
							{description || 'We searched high and low but couldn\'t find what you\'re looking for. Let\'s find a better place for you to go.'}
						</Typography>
					</S.DescriptionContainer>
					{message !== '' && (
						<Typography tag="h4" weight="semibold">
							{message || 'Shop all our products'}
						</Typography>
					)}
				</S.MessageContainer>
				<S.ActionContainer $fullWidth={!buttons}>
					{
						buttons ? buttons.map((button) => (
							<S.ButtonLink to={button.link} key={button.text}>
								<Button size={button.size} variant={button.variant || 'solid'} merchant={merchant}>{button.text}</Button>
							</S.ButtonLink>
						)) : (
							<S.ButtonLink to="/">
								<Button size="large" merchant={merchant}>Explore now</Button>
							</S.ButtonLink>
						)
					}
				</S.ActionContainer>
			</S.ContentContainer>
		</S.Wrapper>
	</>
);


/**
 * Configuration
 */

ErrorComponent.displayName = 'ErrorComponent';
ErrorComponent.propTypes = {
	locale: PropTypes.shape(),
	merchant: PropTypes.shape(),
	title: PropTypes.string,
	description: PropTypes.node,
	message: PropTypes.string,
	buttons: PropTypes.arrayOf(PropTypes.shape())
};
ErrorComponent.defaultProps = {
	locale: {},
	merchant: {},
	title: null,
	description: null,
	message: null,
	buttons: null
};
